/**
 * Services and requests on a customer's page (staff directors and admins): the customer's requests with their
 * threads (reply, status, turn into a quote), the customer's plan, and their quotes and invoice records. The plan is
 * saved under RLS (audited); replies, status changes, quotes and cancelling go through the staff_* database
 * functions, under the staff member's own login so the audit log records who.
 */
import { useState, type FormEvent, type ReactNode } from "react";
import { Icon } from "../components/Icon";
import { plural } from "../components/PageHeader";
import {
  cancelQuote,
  deletePlan,
  openRequestFile,
  quoteRequest,
  savePlan,
  updateRequest,
  type CustomerDetail,
  type QuoteStatus,
  type RequestStatus,
  type StaffQuote,
  type StaffRequest,
} from "../lib/customers-data";
import { aud, fmtDate, fmtPlainDate, localYmd, addDays } from "../lib/format";

type Act = (fn: () => Promise<unknown>, done?: string) => Promise<void>;

export const REQUEST_LABEL: Record<RequestStatus, string> = { received: "Received", quoted: "Quoted", in_progress: "In progress", done: "Done" };
export const REQUEST_TONE: Record<RequestStatus, string> = { received: "accent", quoted: "primary-outline", in_progress: "grey", done: "green" };
const QUOTE_LABEL: Record<QuoteStatus, string> = { sent: "Waiting for the customer", accepted: "Accepted", declined: "Declined", cancelled: "Cancelled" };
const QUOTE_TONE: Record<QuoteStatus, string> = { sent: "accent", accepted: "green", declined: "outline", cancelled: "outline" };
export const URGENCY_LABEL = { low: "Low", normal: "Normal", urgent: "Urgent" } as const;
export const KIND_LABEL = { change: "Change request", support: "Support" } as const;
const GST = __OPS_PORTAL__.servicesGst;

function Field({ label, children, wide }: { label: string; children: ReactNode; wide?: boolean }) {
  return (
    <label className={`field${wide ? " cs-wide" : ""}`}>
      <span className="field__label">{label}</span>
      {children}
    </label>
  );
}

export const UrgencyTag = ({ urgency }: { urgency: StaffRequest["urgency"] }) =>
  urgency === "normal" ? null : <span className={`chip ${urgency === "urgent" ? "chip--danger" : "chip--outline"} cs-gap`}>{URGENCY_LABEL[urgency]}</span>;

/* ------------------------------------------------------------------ requests */

export function CustomerRequests({ data, act }: { data: CustomerDetail; act: Act }) {
  const list = data.services.requests;
  if (!__OPS_PORTAL__.customerRequests) return null;
  const open = list.filter((r) => r.status !== "done");
  return (
    <section className={`card${open.some((r) => r.status === "received") ? " card--alert" : ""}`} aria-labelledby="req-title">
      <div className="card__head">
        <h2 className="card__title" id="req-title">Requests</h2>
        <span className="muted small">{plural(open.length, "open request")}</span>
      </div>
      {list.length === 0 ? (
        <div className="card__body"><p className="muted">No requests yet. Change requests and support questions the customer raises in their portal show here.</p></div>
      ) : (
        <ul className="list">
          {list.map((r) => <RequestRow key={r.id} data={data} request={r} act={act} />)}
        </ul>
      )}
    </section>
  );
}

function RequestRow({ data, request: r, act }: { data: CustomerDetail; request: StaffRequest; act: Act }) {
  const [reply, setReply] = useState("");
  const [status, setStatus] = useState<RequestStatus>(r.status === "received" ? "in_progress" : r.status);
  const quote = r.quote_id ? data.services.quotes.find((q) => q.id === r.quote_id) : undefined;
  return (
    <li className="list__row cs-request" id={`request-${r.id}`}>
      <div className="cs-feedback__main">
        <div className="cs-request__head">
          <span>
            <strong>{r.title}</strong> <span className="muted small">{r.reference}</span>
            <UrgencyTag urgency={r.urgency} />
          </span>
          <span className={`chip chip--${REQUEST_TONE[r.status]}`}>{REQUEST_LABEL[r.status]}</span>
        </div>
        <span className="sub">{KIND_LABEL[r.kind]} from {r.author_name}, {fmtDate(r.created_at)}{r.addon_key ? `. Add-on: ${r.addon_key}` : ""}</span>
        <p className="cs-pre">{r.details}</p>
        {r.page_url && <a className="sub sub--link cs-break" href={r.page_url} target="_blank" rel="noopener noreferrer">{r.page_url}</a>}
        {r.service_request_files.length > 0 && (
          <span className="cs-files">
            {r.service_request_files.map((file) => (
              <button key={file.id} type="button" className="btn btn--outline btn--xs" onClick={() => act(() => openRequestFile(file.id))}>
                <Icon name="file" size={16} /> {file.file_name}
              </button>
            ))}
          </span>
        )}
        {r.service_request_messages.length > 0 && (
          <ol className="cs-thread">
            {r.service_request_messages.map((m) => (
              <li key={m.id} className={`cs-msg cs-msg--${m.author_kind}`}>
                <span className="sub"><strong>{m.author_name}</strong>{m.author_kind === "customer" ? " (customer)" : ""}, {fmtDate(m.created_at)}</span>
                <p className="cs-pre">{m.body}</p>
              </li>
            ))}
          </ol>
        )}
        {quote && (
          <p className="small">
            Quote {quote.quote_number}, {aud(quote.total)}: <span className={`chip chip--${QUOTE_TONE[quote.status]}`}>{QUOTE_LABEL[quote.status]}</span>
          </p>
        )}
        <form
          className="cs-reply"
          onSubmit={(e) => {
            e.preventDefault();
            void act(async () => {
              await updateRequest(r.id, status !== r.status ? status : null, reply.trim() || null);
              setReply("");
            }, reply.trim() ? "Reply sent. The customer sees it in their portal." : "Status saved.");
          }}
        >
          <Field label="Reply to the customer">
            <textarea className="input cs-textarea" rows={2} maxLength={5000} value={reply} onChange={(e) => setReply(e.target.value)} />
          </Field>
          <div className="cs-actions">
            <select className="input input--compact" aria-label={`Status of ${r.reference}`} value={status} onChange={(e) => setStatus(e.target.value as RequestStatus)}>
              {(Object.keys(REQUEST_LABEL) as RequestStatus[]).map((s) => <option key={s} value={s}>{REQUEST_LABEL[s]}</option>)}
            </select>
            <button type="submit" className="btn btn--primary btn--xs" disabled={!reply.trim() && status === r.status}>
              {reply.trim() ? (status !== r.status ? "Send reply and save status" : "Send reply") : "Save status"}
            </button>
          </div>
        </form>
        {__OPS_PORTAL__.customerServices && r.status !== "done" && quote?.status !== "sent" && (
          <details className="cs-details cs-details--inner">
            <summary className="cs-summary"><Icon name="dollar" size={18} /> Turn into a quote</summary>
            <QuoteBuilder request={r} act={act} />
          </details>
        )}
      </div>
    </li>
  );
}

interface LineDraft { description: string; quantity: string; unit: string; unit_price: string }
const blankLine = (description = ""): LineDraft => ({ description, quantity: "1", unit: "", unit_price: "" });
const num = (s: string) => (s.trim() === "" ? NaN : Number(s));

function QuoteBuilder({ request: r, act }: { request: StaffRequest; act: Act }) {
  const [title, setTitle] = useState(r.title);
  const [description, setDescription] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([blankLine(r.title)]);
  const [validUntil, setValidUntil] = useState(addDays(localYmd(), 30));
  const [invoice, setInvoice] = useState(true);
  const [note, setNote] = useState("");
  const parsed = lines.map((l) => ({ description: l.description.trim(), quantity: num(l.quantity), unit: l.unit.trim() || null, unit_price: num(l.unit_price) }));
  const valid = title.trim() && parsed.every((l) => l.description && l.quantity > 0 && l.unit_price >= 0);
  const subtotal = Math.round(parsed.reduce((t, l) => t + (valid ? Math.round(l.quantity * l.unit_price * 100) / 100 : 0), 0) * 100) / 100;
  const gst = GST ? Math.round(subtotal * 10) / 100 : 0;
  const setLine = (i: number, patch: Partial<LineDraft>) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  async function submit(e: FormEvent) {
    e.preventDefault();
    await act(
      () => quoteRequest(r.id, { title: title.trim(), description: description.trim() || null, lines: parsed, validUntil: validUntil || null, invoiceOnAccept: invoice, note: note.trim() || null }),
      "Quote sent. It shows under Services for an owner at the customer to sign, and the request is marked quoted.",
    );
  }

  return (
    <form className="card__body stack" onSubmit={submit}>
      <div className="cs-grid">
        <Field label="Quote title"><input className="input" required maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
        <Field label="Open until"><input className="input" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} /></Field>
        <Field label="What's included (shown to the customer, optional)" wide><textarea className="input cs-textarea" rows={2} maxLength={5000} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
      </div>
      <fieldset className="cs-lines">
        <legend className="field__label">Lines{GST ? " (prices before GST)" : ""}</legend>
        {lines.map((l, i) => (
          <div key={i} className="cs-line">
            <Field label="Description"><input className="input" required maxLength={300} value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} /></Field>
            <Field label="Quantity"><input className="input" required type="number" min={0.01} step={0.01} value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} /></Field>
            <Field label="Unit (optional)"><input className="input" maxLength={30} placeholder="hours" value={l.unit} onChange={(e) => setLine(i, { unit: e.target.value })} /></Field>
            <Field label="Unit price ($)"><input className="input" required type="number" min={0} step={0.01} value={l.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} /></Field>
            {lines.length > 1 && (
              <button type="button" className="btn btn--outline btn--xs cs-line__remove" onClick={() => setLines(lines.filter((_, j) => j !== i))}>Remove</button>
            )}
          </div>
        ))}
        <div><button type="button" className="btn btn--outline btn--xs" onClick={() => setLines([...lines, blankLine()])}><Icon name="plus" size={16} /> Add a line</button></div>
      </fieldset>
      <p className="small">
        {valid ? <>Subtotal {aud(subtotal)}{GST ? `, GST ${aud(gst)}` : ""}, total <strong>{aud(subtotal + gst)}</strong>.</> : "Fill in every line to see the total."}
      </p>
      <Field label="Message on the request (optional)">
        <textarea className="input cs-textarea" rows={2} maxLength={5000} value={note} placeholder="Default: we have sent you a quote, and an owner can sign it under Services." onChange={(e) => setNote(e.target.value)} />
      </Field>
      <label className="cs-check"><input type="checkbox" checked={invoice} onChange={(e) => setInvoice(e.target.checked)} /> Make an invoice record when it is accepted</label>
      <p className="small muted">A quote cannot be changed once sent. To change it, cancel it and send a new one.</p>
      <div><button type="submit" className="btn btn--primary btn--sm" disabled={!valid}>Send quote</button></div>
    </form>
  );
}

/* ------------------------------------------------------------------ plan, quotes and invoices */

export function CustomerPlan({ data, act }: { data: CustomerDetail; act: Act }) {
  if (!__OPS_PORTAL__.customerServices) return null;
  const { plan, quotes, invoices } = data.services;
  const invoiceFor = (q: StaffQuote) => invoices.find((i) => i.service_quote_id === q.id);
  return (
    <section className="card" aria-labelledby="services-title">
      <div className="card__head"><h2 className="card__title" id="services-title">Services</h2></div>
      <div className="card__body stack">
        <h3 className="cs-h3">Current plan</h3>
        {plan ? (
          <p>
            <strong>{plan.name}</strong>
            {plan.monthly_fee !== null && <>, {aud(plan.monthly_fee)} a month{GST ? " plus GST" : ""}</>}
            {plan.modules.length > 0 && <span className="sub">Switched on: {plan.modules.join(", ")}</span>}
          </p>
        ) : (
          <p className="muted">No plan yet. The customer sees "no plan yet" under Services until one is set.</p>
        )}
        <details className="cs-details cs-details--inner">
          <summary className="cs-summary">{plan ? "Edit the plan" : <><Icon name="plus" size={18} /> Set the plan</>}</summary>
          <PlanForm key={plan?.id ?? "new"} data={data} act={act} />
        </details>

        <h3 className="cs-h3">Quotes</h3>
        {quotes.length === 0 ? (
          <p className="muted">No quotes yet. Add-ons the customer chooses, and requests you turn into quotes, show here.</p>
        ) : (
          <ul className="list">
            {quotes.map((q) => {
              const inv = invoiceFor(q);
              return (
                <li key={q.id} className="list__row cs-host">
                  <div className="cs-host__main">
                    <span>
                      <strong>{q.quote_number}: {q.title}</strong>
                      {q.placeholder && <span className="chip chip--outline cs-gap">Placeholder price</span>}
                    </span>
                    <span className="sub">
                      {aud(q.total)}{q.gst_rate > 0 ? " including GST" : ""}. {q.source === "addon" ? "Add-on chosen by the customer" : "From a request"}, {fmtDate(q.created_at)}
                      {q.status === "sent" && q.valid_until ? `. Open until ${fmtPlainDate(q.valid_until)}` : ""}
                      {q.accepted_at ? `. Accepted ${fmtDate(q.accepted_at)}` : ""}
                      {q.decline_reason ? `. Declined: ${q.decline_reason}` : ""}
                    </span>
                    {inv && <span className="sub">Invoice record {inv.reference}, {aud(inv.total)}, {inv.status === "not_synced" ? "not sent to Xero yet" : inv.status}</span>}
                  </div>
                  <span className="cs-login__actions">
                    <span className={`chip chip--${QUOTE_TONE[q.status]}`}>{QUOTE_LABEL[q.status]}</span>
                    {q.status === "sent" && (
                      <button type="button" className="btn btn--outline btn--xs" onClick={() => window.confirm(`Cancel ${q.quote_number}? The customer can no longer sign it.`) && act(() => cancelQuote(q.id), "Quote cancelled.")}>
                        Cancel quote
                      </button>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        <p className="small muted">Invoice records are made when a customer accepts a quote. They are not sent to Xero yet.</p>
      </div>
    </section>
  );
}

function PlanForm({ data, act }: { data: CustomerDetail; act: Act }) {
  const plan = data.services.plan;
  const [f, setF] = useState({
    name: plan?.name ?? "",
    description: plan?.description ?? "",
    monthly_fee: plan?.monthly_fee === null || plan?.monthly_fee === undefined ? "" : String(plan.monthly_fee),
    modules: (plan?.modules ?? []).join("\n"),
    starts_on: plan?.starts_on ?? "",
  });
  async function submit(e: FormEvent) {
    e.preventDefault();
    const fee = f.monthly_fee.trim() === "" ? null : Number(f.monthly_fee);
    await act(
      () => savePlan(data.customer.id, plan?.id ?? null, {
        name: f.name.trim(),
        description: f.description.trim() || null,
        monthly_fee: fee,
        modules: f.modules.split(/[\n,]/).map((m) => m.trim()).filter(Boolean).slice(0, 30),
        starts_on: f.starts_on || null,
      }),
      "Plan saved. The customer sees it under Services.",
    );
  }
  return (
    <form className="card__body stack" onSubmit={submit}>
      <div className="cs-grid">
        <Field label="Plan name"><input className="input" required maxLength={200} value={f.name} placeholder="For example, Care plan" onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label={`Monthly fee ($${GST ? ", before GST" : ""}, optional)`}><input className="input" type="number" min={0} step={0.01} value={f.monthly_fee} onChange={(e) => setF({ ...f, monthly_fee: e.target.value })} /></Field>
        <Field label="Since (optional)"><input className="input" type="date" value={f.starts_on} onChange={(e) => setF({ ...f, starts_on: e.target.value })} /></Field>
        <Field label="What it covers (shown to the customer, optional)" wide><textarea className="input cs-textarea" rows={2} maxLength={2000} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        <Field label="Modules switched on (one a line)" wide><textarea className="input cs-textarea" rows={3} value={f.modules} placeholder={"Website\nCustomer portal"} onChange={(e) => setF({ ...f, modules: e.target.value })} /></Field>
      </div>
      <div className="cs-actions">
        <button type="submit" className="btn btn--primary btn--sm">Save plan</button>
        {plan && (
          <button type="button" className="btn btn--outline btn--sm" onClick={() => window.confirm("Remove this plan? The customer will see no plan yet.") && act(() => deletePlan(plan.id), "Plan removed.")}>
            Remove plan
          </button>
        )}
      </div>
    </form>
  );
}
